import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import VideoCard from "@/components/feature/VideoCard";
import SectionHeading from "@/components/feature/SectionHeading";
import PlayerDemo from "./components/PlayerDemo";
import { categoryMeta, getVideoById, videos } from "@/mocks/videos";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function VideoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const video = id ? getVideoById(id) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [copied, setCopied] = useState(false);

  usePageMeta({
    title: video ? `${video.title} - 高清视频免费观看` : "视频详情 - 高清视频免费观看",
    description: video
      ? `${video.title}（${video.year}·${video.region}）在线观看：${video.description.slice(0, 60)}，尽在高清视频免费观看。`
      : "高清视频免费观看，海量高清影视资源在线播放。",
    keywords: video ? `${video.title},${video.title}在线观看,${video.categoryLabel},高清视频免费观看` : "高清视频免费观看",
  });

  const related = useMemo(() => {
    if (!video) return [];
    const sameCategory = videos.filter((v) => v.category === video.category && v.id !== video.id);
    const tagMatched = videos.filter(
      (v) => v.id !== video.id && v.tags.some((tag) => video.tags.includes(tag))
    );
    const merged = [...sameCategory, ...tagMatched].filter(
      (v, idx, arr) => arr.findIndex((x) => x.id === v.id) === idx
    );
    return merged.slice(0, 6);
  }, [video]);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
    } catch {
      setCopied(true);
    }
    window.setTimeout(() => setCopied(false), 2000);
  };

  if (!video) {
    return (
      <div className="bg-background-100 min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center">
          <span className="w-16 h-16 mx-auto rounded-full bg-secondary-100 text-secondary-600 flex items-center justify-center">
            <i className="ri-file-unknow-line text-3xl"></i>
          </span>
          <h1 className="font-heading font-bold text-xl text-foreground-950 mt-4">未找到该视频</h1>
          <p className="text-sm text-foreground-500 mt-2">你访问的视频可能已下架或链接有误。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const meta = categoryMeta[video.category];
  const favorited = isFavorite(video.id);

  return (
    <div className="bg-background-100">
      <div className="max-w-[1360px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        <Breadcrumbs
          items={[{ label: "首页", to: "/" }, { label: meta.label, to: `/category/${meta.slug}` }, { label: video.title }]}
        />

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2">
            <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">{video.title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs md:text-sm">
              <span className="px-2 py-0.5 rounded-md bg-primary-100 text-primary-700 font-medium">{video.categoryLabel}</span>
              <span className="text-foreground-500">{video.year}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span className="text-foreground-500">{video.region}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span className="text-foreground-500">{video.duration}</span>
              <span className="flex items-center gap-1 text-accent-600 font-semibold">
                <i className="ri-star-fill"></i>
                {video.rating}
              </span>
            </div>

            <div className="mt-5">
              <PlayerDemo video={video} />
            </div>

            <div className="mt-6 rounded-lg bg-background-50 border border-background-200 p-5 md:p-6">
              <h2 className="font-heading font-bold text-lg text-foreground-950 flex items-center gap-2">
                <i className="ri-file-text-line text-primary-500"></i>
                剧情简介
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">{video.description}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {video.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-800 text-xs">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <div className="rounded-lg overflow-hidden bg-background-50 border border-background-200">
                <div className="relative w-full aspect-[2/3] overflow-hidden bg-background-200">
                  <img
                    src={video.cover}
                    alt={`${video.title} 高清视频封面`}
                    title={`${video.title} 高清视频在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => toggleFavorite(video.id)}
                      className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold transition-colors whitespace-nowrap ${
                        favorited
                          ? "bg-accent-500 text-white hover:bg-accent-600"
                          : "border border-background-300 text-foreground-700 hover:border-accent-400 hover:text-accent-600"
                      }`}
                    >
                      <i className={`${favorited ? "ri-heart-fill" : "ri-heart-line"} text-lg`}></i>
                      {favorited ? "已收藏" : "收藏"}
                    </button>
                    <button
                      type="button"
                      onClick={handleShare}
                      className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-share-line text-lg"></i>
                      分享
                    </button>
                  </div>
                  {copied && (
                    <p className="mt-3 text-xs text-primary-600 flex items-center gap-1 animate-fade-in">
                      <i className="ri-checkbox-circle-line"></i>
                      链接已复制到剪贴板
                    </p>
                  )}

                  <dl className="mt-5 space-y-3 text-sm">
                    {[
                      { label: "导演", value: video.director },
                      { label: "主演", value: video.cast.join("、") },
                      { label: "上映时间", value: `${video.year}年` },
                      { label: "地区", value: video.region },
                      { label: "类型", value: video.categoryLabel },
                      { label: "上线日期", value: video.publishDate },
                    ].map((row) => (
                      <div key={row.label} className="flex gap-3">
                        <dt className="text-foreground-400 w-16 shrink-0">{row.label}</dt>
                        <dd className="text-foreground-800">{row.value}</dd>
                      </div>
                    ))}
                  </dl>

                  <Link
                    to={`/category/${meta.slug}`}
                    className="mt-5 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-colors"
                  >
                    <i className="ri-arrow-left-line"></i>
                    返回{meta.label}分类
                  </Link>
                </div>
              </div>
            </div>
          </aside>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-12 md:mt-16">
            <SectionHeading
              icon="ri-thumb-up-line"
              title="相关推荐"
              subtitle="根据类别与题材为你精选"
              linkTo={`/category/${meta.slug}`}
              linkLabel={`更多${meta.label}`}
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {related.map((item) => (
                <VideoCard key={item.id} video={item} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}